import Echo from 'laravel-echo';

import Pusher from 'pusher-js';
window.Pusher = Pusher;

// Only initialize Echo if Reverb is fully configured
const reverbKey = import.meta.env.VITE_REVERB_APP_KEY;
const reverbHost = import.meta.env.VITE_REVERB_HOST;
let reverbPort = import.meta.env.VITE_REVERB_PORT;
const reverbScheme = import.meta.env.VITE_REVERB_SCHEME ?? 'https';

// Debug: log what we're getting
if (import.meta.env.DEV) {
    console.log('Echo config check:', {
        hasKey: !!reverbKey,
        hasHost: !!reverbHost,
        port: reverbPort,
        scheme: reverbScheme,
    });
}

// In local dev with a .test/localhost host, skip connecting so we don't spam WebSocket errors when Reverb isn't running.
const isLocalReverbHost = reverbHost && (
    reverbHost.includes('.test') ||
    reverbHost.includes('localhost') ||
    reverbHost === '127.0.0.1'
);
const skipConnect = import.meta.env.DEV && isLocalReverbHost;

// Only initialize if we have key and host (port is optional, will use defaults)
if (reverbKey && reverbHost && !skipConnect) {
    try {
        const echoConfig = {
            broadcaster: 'reverb',
            key: reverbKey,
            wsHost: reverbHost,
            enabledTransports: ['ws', 'wss'],
        };

        // Only set ports if they're defined and valid
        if (reverbPort && reverbPort !== 'undefined' && reverbPort !== 'null') {
            const portNum = parseInt(reverbPort, 10);
            if (!isNaN(portNum) && portNum > 0) {
                echoConfig.wsPort = portNum;
                echoConfig.wssPort = portNum;
            }
        }
        // If no port specified, Echo will use defaults (80 for ws, 443 for wss)

        // Set TLS based on scheme
        echoConfig.forceTLS = reverbScheme === 'https';

        window.Echo = new Echo(echoConfig);

        // Add error handler to prevent crashes
        if (window.Echo && window.Echo.connector && window.Echo.connector.pusher) {
            const pusher = window.Echo.connector.pusher;
            
            // Prevent connection attempts if config is invalid
            if (pusher.connection) {
                pusher.connection.bind('error', (err) => {
                    console.warn('Echo connection error (non-fatal):', err);
                });

                pusher.connection.bind('disconnected', () => {
                    console.warn('Echo disconnected (non-fatal)');
                });

                pusher.connection.bind('unavailable', () => {
                    console.warn('Echo connection unavailable (non-fatal)');
                });
            }
        }
    } catch (error) {
        console.warn('Failed to initialize Echo (non-fatal):', error);
        // Create a dummy Echo object to prevent errors in components that check for window.Echo
        window.Echo = createDummyEcho();
    }
} else {
    if (import.meta.env.DEV && !skipConnect) {
        console.warn('Echo not initialized: Reverb configuration incomplete', {
            hasKey: !!reverbKey,
            hasHost: !!reverbHost,
        });
    }
    if (import.meta.env.DEV && skipConnect) {
        console.log('Echo: using dummy (local Reverb host) to avoid WebSocket errors when Reverb is not running.');
    }
    // Create a dummy Echo object to prevent errors in components that check for window.Echo
    window.Echo = createDummyEcho();
}

// Helper function to create a dummy Echo object
function createDummyEcho() {
    return {
        private: () => ({
            listen: () => ({ error: () => {} }),
            error: () => {},
        }),
        leave: () => {},
        connector: null,
    };
}
